import { IconAlertTriangle, IconCheck, IconCloudOff, IconLoader2, IconX } from "@tabler/icons-react"
import { type FC, useState } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import Button, { actionClass } from "~/components/Button"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { query } from "~/utilities/trpc"

import DomainHeading from "./DomainHeading"

const dnsRecordColumns = [
	{ label: "Type", flex: { grow: 0, basis: 56 } },
	{ label: "Name", flex: { grow: 1, basis: 120 } },
	{ label: "Value", flex: { grow: 3, basis: 180 } },
]

const connectionRecordColumns = [
	...dnsRecordColumns,
	{ label: "Proxy", flex: { grow: 0, basis: 88 } },
	{ label: "Connected", flex: { grow: 0, basis: 96 } },
]

type Props = {
	domain: {
		id: number
		domain: string
		platform: { subdomain: string }
		landingPage: {
			domain: string
			isConnected: boolean
			verificationRecords: { type: string; host: string; value: string }[]
		} | null
		isSelected: boolean
		records: { type: string; host: string; value: string; isConnected: boolean }[]
	}
	reload(): Promise<void>
}
const ManualDomain: FC<Props> = ({ domain, reload }) => {
	const alert = useAlert()

	const [recordsToRemove, setRecordsToRemove] = useState<{ type: "A" | "CNAME"; host: string; value: string }[]>([])

	const setSelected = query.domain.setSelected.useMutation({
		onSuccess: reload,
		onError(error, variables) {
			if (variables.domain !== null) {
				console.error(`Failed to select domain ${variables.domain}!`, error)
			} else {
				console.error("Failed to deselect domain!", error)
			}
		},
	})

	const check = query.domain.checkManual.useMutation({
		async onSuccess(data) {
			setRecordsToRemove(data.remove)
			await reload()
		},
		onError(error, variables) {
			console.error(`Failed to check domain ${variables.domain}!`, error)
		},
	})

	const changeSubdomain = query.domain.updateManual.useMutation({
		onSuccess: reload,
		onError(error, variables) {
			console.error(
				`Failed to change subdomain to ${variables.platformSubdomain} for domain ${variables.id}!`,
				error,
			)
		},
	})

	const remove = query.domain.deleteManual.useMutation({
		onSuccess: reload,
		onError(error, variables) {
			console.error(`Failed to delete domain ${variables.domain}!`, error)
		},
	})

	async function handleChangeSubdomain() {
		const result = await alert.input(
			"Change subdomain",
			`Enter a new subdomain for the domain **${domain.domain}**`,
			"Subdomain",
			domain.platform.subdomain,
		)
		if (result.result === null) return

		await changeSubdomain.mutateAsync({ id: domain.id, platformSubdomain: result.result.trim() })

		result.close()
	}

	const isConnected = domain.records.every(x => x.isConnected)
	const isFullyConnected = isConnected && (!domain.landingPage || domain.landingPage.isConnected)
	const verificationRecords = domain.landingPage?.verificationRecords ?? []
	const verificationRecordLabel = verificationRecords.length === 1 ? "record" : "records"

	return (
		<div className="border-component-border overflow-hidden rounded-2xl border bg-white">
			<div className="p-4 sm:p-5">
				<DomainHeading
					domain={domain.domain}
					subdomain={domain.platform.subdomain}
					isSelected={domain.isSelected}
					description="Managed at an external registrar"
				/>
			</div>
			<div className="border-component-border bg-component-background/40 border-t px-4 py-3 sm:px-5">
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						onClick={() => remove.mutate({ domain: domain.domain })}
						disabled={remove.isPending}
						className={cn(actionClass, "text-danger")}
					>
						{remove.isPending && <IconLoader2 className="size-3 animate-spin" />}
						Delete
					</button>
					<button type="button" onClick={handleChangeSubdomain} className={actionClass}>
						Change subdomain
					</button>
					{domain.isSelected ? (
						<button
							type="button"
							onClick={() => setSelected.mutate({ domain: null })}
							disabled={setSelected.isPending}
							className={cn(actionClass, "text-danger")}
						>
							{setSelected.isPending && <IconLoader2 className="size-3 animate-spin" />}
							Disconnect
						</button>
					) : (
						<button
							type="button"
							onClick={() => setSelected.mutate({ domain: domain.domain })}
							disabled={setSelected.isPending}
							className={cn(actionClass, "border-primary/20 text-primary")}
						>
							{setSelected.isPending && <IconLoader2 className="size-3 animate-spin" />}
							Publish
						</button>
					)}
				</div>
			</div>
			<div className="border-component-border flex min-w-0 flex-col items-start gap-4 border-t p-4 sm:p-5">
				<div className="flex w-full flex-wrap items-center justify-between gap-2">
					<h3 className="text-sm font-semibold">DNS configuration</h3>
					<span
						className={cn(
							"rounded px-2 py-1 text-xs font-medium",
							isFullyConnected ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
						)}
					>
						{isFullyConnected ? "Connected" : "Setup required"}
					</span>
				</div>
				{!isConnected && (
					<div className="flex flex-col gap-2 text-sm leading-relaxed text-gray-500">
						<p>
							Please log in to your domain registrar's admin console (the website where you purchased the
							domain) and add the following DNS records.
						</p>
						<p>If prompted to set a TTL (Time To Live), use the default value or set it to 3600.</p>
					</div>
				)}
				{verificationRecords.length > 0 && (
					<div className="flex w-full flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
						<div className="flex items-start gap-3">
							<IconAlertTriangle className="mt-0.5 size-5 shrink-0" />
							<div className="flex flex-col gap-1">
								<p className="font-semibold">Website ownership verification is required</p>
								<p>
									Your DNS records may already point to Forento, but your website domain is not
									connected until ownership verification is complete. Add the verification{" "}
									{verificationRecordLabel} below at your DNS provider. You can remove{" "}
									{verificationRecords.length === 1 ? "it" : "them"} after verification is complete.
								</p>
							</div>
						</div>
						<Table
							columns={dnsRecordColumns}
							data={verificationRecords.map(({ type, host, value }) => [type, host, value])}
							isCompact
						/>
					</div>
				)}
				<Table
					columns={connectionRecordColumns}
					data={domain.records.map(({ type, host, value, isConnected }) => [
						type,
						host,
						value,
						<div key={`${type}-${host}-proxy`} className="text-light flex items-center gap-1">
							<IconCloudOff className="size-4" />
							Disabled
						</div>,
						<div
							key={`${type}-${host}-connected`}
							className={cn("flex items-center gap-1", isConnected ? "text-success" : "text-danger")}
						>
							{isConnected ? (
								<>
									<IconCheck className="size-4" /> Yes
								</>
							) : (
								<>
									<IconX className="size-4" /> No
								</>
							)}
						</div>,
					])}
					isCompact
				/>
				{recordsToRemove.length > 0 && (
					<>
						<p>
							The following DNS records conflict with the platform's DNS records and need to be removed.
						</p>
						<Table
							columns={dnsRecordColumns}
							data={recordsToRemove.map(({ type, host, value }) => [type, host, value])}
						/>
					</>
				)}
				{!isFullyConnected && (
					<Button
						variant="primary"
						onClick={() => check.mutate({ domain: domain.domain })}
						isLoading={check.isPending}
					>
						Check connection
					</Button>
				)}
			</div>
		</div>
	)
}

export default ManualDomain
